import { HgCountry, HgRegion, HgState } from "../../../types/taxonomy";
import { SOMConfig } from "../../../types/workspace";
import { abbrevNumberFormat } from "../../../utils/numberUtils";
import {
  FilteredData,
  FilteredGeographyData,
  FilterLabel,
  IndustryNameData,
} from "./types";

interface CountResult {
  includedCount: number;
  excludedCount: number;
}

interface RangeData {
  min?: number;
  max?: number;
}

const generateRangeLabel = (
  range: RangeData | undefined,
  type: "Revenue" | "Employees"
): FilterLabel | undefined => {
  if (!range || (!range.min && !range.max)) {
    return undefined;
  }

  let text = "";
  if (range.min && range.max) {
    text = `${type} Between ${abbrevNumberFormat(range.min)} and ${abbrevNumberFormat(range.max)}`;
  } else if (range.min) {
    text = `${type} > ${abbrevNumberFormat(range.min)}`;
  } else if (range.max) {
    text = `${type} < ${abbrevNumberFormat(range.max)}`;
  }

  return text
    ? { text, type: type.toLowerCase() as "revenue" | "employee" }
    : undefined;
};

/**
 * Get total counts from either geography or industry data
 */
const calculateTotalCounts = (
  data: (
    | FilteredData<HgCountry>
    | FilteredData<HgRegion>
    | FilteredData<HgState>
    | FilteredData<string>
  )[]
): CountResult => {
  const includedCount = data.reduce(
    (acc, item) => acc + item.included.length,
    0
  );
  const excludedCount = data.reduce(
    (acc, item) => acc + item.excluded.length,
    0
  );

  return { includedCount, excludedCount };
};

export const getTotalCounts = (
  data: FilteredGeographyData | IndustryNameData
): CountResult => {
  return "countries" in data
    ? calculateTotalCounts([
        data.states.included.length > 0
          ? { included: [], excluded: [] }
          : data.countries,
        data.regions,
        data.states,
      ])
    : calculateTotalCounts([data.hgData, data.sicData, data.naicsData]);
};

/**
 * Generate label text based on counts
 */
export const getLabelText = (
  type: "Geography" | "Industries" | "Products",
  includedCount: number,
  excludedCount: number
): string => {
  if (includedCount === 0 && excludedCount === 0) {
    return "";
  }

  const parts = [];
  if (includedCount > 0) {
    parts.push(`${includedCount} included`);
  }
  if (excludedCount > 0) {
    parts.push(`${excludedCount} excluded`);
  }

  return parts.length > 0 ? `${type}: ${parts.join(", ")}` : "";
};

export const getEmployeeAndRevenueRangeLabel = (configuration?: SOMConfig) => {
  // Check if workspace is defined
  if (!configuration) {
    return [];
  }

  const allLabels: FilterLabel[] = [];

  const employeeLabel = generateRangeLabel(
    configuration.employeeRange,
    "Employees"
  );
  if (employeeLabel) {
    allLabels.push(employeeLabel);
  }
  const revenueLabel = generateRangeLabel(
    configuration.revenueRange,
    "Revenue"
  );
  if (revenueLabel) {
    allLabels.push(revenueLabel);
  }

  return allLabels;
};
